// ─────────────────────────────────────────────────────────────────────────────
// SALES SJT — Question Bank
// 20 scenarios · Most/Least effective format · 20 min
// Science: Wagner & Sternberg (1990) tacit knowledge · Goal-orientation theory
// Market: BFSI · Pharma MR · FMCG DSA · Insurance agents · India volume
// ─────────────────────────────────────────────────────────────────────────────

export const SJT_SALES_ITEMS = [

  // ── OBJECTION HANDLING ────────────────────────────────────────────────────
  {
    id:"sl01", d:1, category:"objections",
    context:"You are a field sales executive selling insurance. A prospect says: 'I already have insurance from my bank. I don't need another policy.' You know their bank policy likely has significant coverage gaps.",
    opts:[
      "A: Agree and move on — he has already decided",
      "B: Acknowledge his existing cover, ask a few specific questions about what it covers, and if you find gaps, present your product as a complement not a replacement — backed by a side-by-side comparison",
      "C: Tell him his bank's product is inferior and he is making a mistake",
      "D: Ask him if he has read his policy document recently and offer to review it together",
    ],
    most:1, least:2,
    exp_most:"Acknowledge, probe for gaps, then position as a complement — not a replacement. This respects the prospect and builds trust.",
    exp_least:"Criticising a competitor product directly damages rapport and credibility."
  },
  {
    id:"sl02", d:2, category:"objections",
    context:"A doctor (your Pharma MR customer) says: 'Your tablet is too expensive. The generic version does the same job.' You have clinical data showing superior bioavailability.",
    opts:[
      "A: Agree that price is important and offer a discount immediately",
      "B: Acknowledge the cost concern, then pivot to the clinical data — explain the bioavailability difference and the real-world patient outcome implications, and offer to leave the study for their review",
      "C: Tell the doctor that the generic is not as good and that they should know better",
      "D: Tell him you will check with your manager about pricing and come back",
      ],
    most:1, least:2,
    exp_most:"Acknowledge the concern, then pivot to clinical evidence with a leave-behind — professional, evidence-based, respectful.",
    exp_least:"Questioning a doctor's clinical knowledge is disrespectful and ruins the relationship."
  },
  {
    id:"sl03", d:2, category:"objections",
    context:"An FMCG retailer says: 'Your competitor gives me better margins. Why should I stock your brand?' Your margins are fixed but you have stronger brand pull and superior merchandising support.",
    opts:[
      "A: Match the competitor's margin offer even though you are not authorised to",
      "B: Acknowledge the margin gap honestly, then shift the conversation to total value — brand offtake data, consumer pull, faster stock rotation, and the merchandising support you provide",
      "C: Tell him the competitor is running unsustainable promotions that will end soon",
      "D: Walk away — if margins are his only concern, he is not worth pursuing",
    ],
    most:1, least:0,
    exp_most:"Honest acknowledgement of the gap followed by total value repositioning is the professional consultative approach.",
    exp_least:"Offering unauthorised discounts creates legal risk, sets a bad precedent and undermines company pricing."
  },
  {
    id:"sl04", d:3, category:"objections",
    context:"A key account prospect has been 'thinking about it' for six weeks. Every time you call, they say they need more time. Your pipeline review is next week.",
    opts:[
      "A: Keep calling weekly and waiting patiently",
      "B: Send a detailed email with a summary of everything discussed and ask for a specific decision date — if no response, escalate internally to see if a senior stakeholder engagement is needed",
      "C: Create urgency by telling them a special pricing window is closing",
      "D: Ask directly: 'Is there something specific holding you back that we haven't addressed?' and listen carefully to the answer",
    ],
    most:3, least:2,
    exp_most:"Asking directly about the specific barrier is the most efficient way to identify and address a hidden objection.",
    exp_least:"Creating false urgency through a fabricated pricing deadline is manipulative and damages trust when discovered."
  },

  // ── PIPELINE & PROSPECTING ────────────────────────────────────────────────
  {
    id:"sl05", d:2, category:"pipeline",
    context:"You have hit your monthly target by the 20th of the month. You have 10 days left. Three warm leads are still in progress.",
    opts:[
      "A: Slow down — you have hit your target and can relax",
      "B: Keep working the warm leads, document everything for next month's pipeline, and use the remaining time to prospect for new leads — next month's performance starts today",
      "C: Push all three warm leads to close this month even if the timing is not right for them",
      "D: Take a few days off — you have earned it",
    ],
    most:1, least:2,
    exp_most:"Continuing to prospect and build pipeline consistency demonstrates a professional sales mindset beyond quota.",
    exp_least:"Pushing clients to close before they are ready destroys relationships and leads to early cancellations or returns."
  },
  {
    id:"sl06", d:1, category:"pipeline",
    context:"You are a new FMCG territory sales rep. Your territory has 80 potential outlets. You need to prioritise which to visit first in your first month.",
    opts:[
      "A: Visit outlets alphabetically to be systematic",
      "B: Visit the nearest outlets first to save travel time",
      "C: Analyse outlets by category — high-volume, currently stocking competitor, recently churned — and prioritise by revenue potential and win probability",
      "D: Ask your manager to tell you which outlets to visit",
    ],
    most:2, least:0,
    exp_most:"Analytical prioritisation by revenue potential and win probability is fundamental sales territory management.",
    exp_least:"Alphabetical sequencing ignores all commercial logic and wastes high-opportunity time."
  },
  {
    id:"sl07", d:2, category:"pipeline",
    context:"A high-value prospect requests a proposal. After you send it, they go completely silent for two weeks — no response to calls or emails.",
    opts:[
      "A: Stop chasing — they are not interested",
      "B: Send one more email saying you will assume they are not interested if you don't hear back by a specific date",
      "C: Try a different channel — LinkedIn or an in-person visit — to reconnect, and lead with value (a relevant insight or question) rather than chasing for an answer",
      "D: Send a revised proposal with a lower price to re-engage",
    ],
    most:2, least:3,
    exp_most:"Multi-channel outreach with a value-first approach breaks through without being pushy.",
    exp_least:"Unsolicited price reduction signals desperation and devalues your offering before any negotiation has happened."
  },
  {
    id:"sl08", d:3, category:"pipeline",
    context:"You are a BFSI relationship manager. A client calls to inform you they are moving 40% of their portfolio to a competitor. They seem open to discussion.",
    opts:[
      "A: Thank them for informing you and wish them well",
      "B: Immediately offer a rate match without understanding why they are leaving",
      "C: Thank them for calling first, ask open questions to understand the real reason they are considering the move, address the specific concern if you can, and if not — acknowledge it honestly and explore what would make them stay",
      "D: Escalate immediately to your senior without having any conversation with the client yourself",
    ],
    most:2, least:1,
    exp_most:"Probing to understand the real reason for leaving — not assuming it is price — is the foundation of effective retention.",
    exp_least:"Immediate rate match without understanding the issue may be irrelevant (it may not be a price issue) and signals panic."
  },

  // ── CLIENT TRUST & RELATIONSHIP ───────────────────────────────────────────
  {
    id:"sl09", d:1, category:"trust",
    context:"A prospect asks you a product question you genuinely don't know the answer to. It's an important technical detail.",
    opts:[
      "A: Make up a plausible answer to appear knowledgeable",
      "B: Tell them that's a great question, admit you want to give them an accurate answer, commit to reverting within 24 hours with the correct information and follow through",
      "C: Change the subject and move on",
      "D: Tell them to check your company website",
    ],
    most:1, least:0,
    exp_most:"Admitting you don't know and following through builds more trust than a confident guess.",
    exp_least:"Making up an answer risks misinformation, damages credibility when discovered and can create legal liability."
  },
  {
    id:"sl10", d:2, category:"trust",
    context:"You are closing a deal and the client asks you to add a specific feature to the contract that you know your product currently doesn't support well.",
    opts:[
      "A: Agree to include the feature to close the deal and figure it out later",
      "B: Tell the client honestly that this specific feature is not your strongest area, explain what you CAN guarantee, and explore whether an alternative solution meets their underlying need",
      "C: Tell the client the feature exists and brief your implementation team afterwards",
      "D: Deflect the question and focus on the parts of the product that work well",
    ],
    most:1, least:0,
    exp_most:"Honest disclosure builds a sustainable relationship. Overselling on features that don't deliver creates churn and reputational damage.",
    exp_least:"Agreeing to a feature you can't deliver is a misrepresentation — a serious professional and legal failure."
  },
  {
    id:"sl11", d:2, category:"trust",
    context:"A long-term client calls upset. They say your competitor has offered them the same product at 15% below your current price. They want you to match it.",
    opts:[
      "A: Immediately offer to match the price",
      "B: Thank them for coming to you first. Ask to see the competitor offer if possible. If legitimate, discuss the full value picture — service, relationship, reliability — then work within your authority on pricing and escalate if needed with a business case",
      "C: Tell them the competitor's offer is probably a promotional trick that won't last",
      "D: Tell them you can't match the price and they should make their own decision",
    ],
    most:1, least:2,
    exp_most:"Value-first discussion before jumping to price matching retains margin and demonstrates relationship confidence.",
    exp_least:"Dismissing a legitimate competitor offer damages credibility and doesn't address the client's actual concern."
  },
  {
    id:"sl12", d:3, category:"trust",
    context:"A client has been allocated to you after your predecessor left. The client was clearly very close to your predecessor and is cold and unwelcoming towards you.",
    opts:[
      "A: Give them space and wait for them to come to you",
      "B: Acknowledge the transition directly and respectfully, express genuine interest in understanding their business and needs from scratch, and focus on demonstrating value through action rather than words",
      "C: Tell them you understand they miss your predecessor but you are here now",
      "D: Involve your manager immediately to help manage the difficult client",
    ],
    most:1, least:2,
    exp_most:"Acknowledging the transition with genuine curiosity about the client's needs is the right tone — earn trust through action.",
    exp_least:"Implying the client should 'move on' from a valued predecessor is tone-deaf and further damages the relationship."
  },

  // ── NEGOTIATION ───────────────────────────────────────────────────────────
  {
    id:"sl13", d:2, category:"negotiation",
    context:"You are in final negotiation with a buyer who says: 'You need to come down 10% or we can't do the deal.' You are already at the bottom of your authorised discount band.",
    opts:[
      "A: Agree to the 10% to close the deal",
      "B: Tell them firmly this is your best price and you're walking away",
      "C: Acknowledge the gap, clarify what is and isn't possible on price, and explore non-price levers — payment terms, volume commitment, extended warranty, additional service — to bridge the gap creatively",
      "D: Tell them you need to speak to your manager and stall",
    ],
    most:2, least:0,
    exp_most:"Exploring non-price value levers when price is exhausted is fundamental professional negotiation.",
    exp_least:"Agreeing to unauthorised discounts undermines pricing integrity and creates a precedent for future negotiations."
  },
  {
    id:"sl14", d:3, category:"negotiation",
    context:"During negotiation, the client mentions a specific issue their business is struggling with that is unrelated to your product but you happen to know someone who can help them.",
    opts:[
      "A: Stay focused on your product — the relationship issue is not your concern",
      "B: Note the issue, and after the meeting, connect them with the person who can help — no conditions, no immediate expectation of return",
      "C: Tell them you can help with that too, even though you can't, to deepen the relationship",
      "D: Use the information as leverage — offer the connection only if they proceed with the deal",
    ],
    most:1, least:3,
    exp_most:"Unconditional value giving builds deep trust and long-term client loyalty — one of the most effective long-term sales behaviours.",
    exp_least:"Using a favour as transactional leverage poisons the relationship and reveals purely self-serving intent."
  },
  {
    id:"sl15", d:2, category:"negotiation",
    context:"The client says they want to 'sleep on it' after what felt like a strong close meeting. You believe the deal is 80% likely to close but you are not sure what is holding them back.",
    opts:[
      "A: Say 'of course, take your time' and wait",
      "B: Ask directly: 'Absolutely — is there a specific concern you'd like me to address before you decide?' and listen to the answer",
      "C: Tell them the offer expires tomorrow to create urgency",
      "D: Follow up immediately the next morning with a revised and improved offer",
    ],
    most:1, least:2,
    exp_most:"Asking a direct question about remaining concerns before the meeting ends is the highest-probability action.",
    exp_least:"Fabricating an offer expiry creates false urgency — a manipulation tactic that damages trust when discovered."
  },

  // ── RESILIENCE & MINDSET ──────────────────────────────────────────────────
  {
    id:"sl16", d:1, category:"resilience",
    context:"You have had three consecutive days of rejections and no conversions. You have a full schedule of calls tomorrow.",
    opts:[
      "A: Call in sick tomorrow — you need a break",
      "B: Review what is and isn't working, identify if there is a pattern to the rejections, adjust your approach based on what you learn, and go into tomorrow with a specific hypothesis to test",
      "C: Push even harder tomorrow with the same approach — volume will eventually work",
      "D: Tell your manager the territory is not good and ask to be moved",
    ],
    most:1, least:3,
    exp_most:"Analytical reflection followed by a hypothesis-driven adjustment is the mark of a resilient, growth-oriented sales professional.",
    exp_least:"Attributing failure to the territory without self-reflection is a fundamental sales mindset failure."
  },
  {
    id:"sl17", d:2, category:"resilience",
    context:"You lost a large deal to a competitor despite a strong pitch. The client gives you brief feedback that the competitor's 'team felt more senior and experienced'.",
    opts:[
      "A: Accept the feedback and move on",
      "B: Thank the client for the feedback, ask one or two follow-up questions to understand what specifically gave that impression, and use the insight to improve future pitches",
      "C: Tell the client that you are actually more experienced than the competitor's team",
      "D: Write off the client — they made the wrong decision",
    ],
    most:1, least:2,
    exp_most:"Probing for specific feedback from a lost deal is among the most valuable sales learning behaviours.",
    exp_least:"Arguing about your experience after losing a deal burns the bridge for any future opportunity."
  },
  {
    id:"sl18", d:3, category:"resilience",
    context:"Your manager reviews your pipeline and says you are 30% below your quarterly forecast with two weeks to go. You believe some deals will close but the timeline is genuinely uncertain.",
    opts:[
      "A: Promise your manager you will hit the number",
      "B: Be transparent about the pipeline reality, share your honest probability assessment for each deal, identify what specific actions could accelerate the most likely deals, and commit to those actions",
      "C: Pull in smaller deals quickly to make the number look better even if they are lower quality",
      "D: Tell your manager the forecast was unrealistic from the start",
    ],
    most:1, least:2,
    exp_most:"Honest pipeline assessment with a clear action plan is the professional response — it builds manager trust and focuses effort.",
    exp_least:"Pulling in low-quality deals to inflate numbers creates churn, unhappy clients and pipeline distortion next quarter."
  },

  // ── ETHICS & COMPLIANCE ───────────────────────────────────────────────────
  {
    id:"sl19", d:2, category:"ethics",
    context:"A client informally hints that they would be more likely to place a large order if there was 'something extra' in it for them personally.",
    opts:[
      "A: Offer a small personal gift — it is just relationship building",
      "B: Ignore the hint and move on",
      "C: Clearly and professionally decline any personal arrangement, refocus on the legitimate commercial value of your offer, and report the incident to your compliance or sales manager",
      "D: Pretend you didn't understand the hint and provide a standard offer",
    ],
    most:2, least:0,
    exp_most:"Declining clearly, refocusing on legitimate value, and reporting is the correct ethical and compliance response.",
    exp_least:"Providing a personal gift in response to a hint is a bribe — a serious legal and professional violation."
  },
  {
    id:"sl20", d:3, category:"ethics",
    context:"You overhear a colleague misrepresenting a product's clinical data to a doctor to win a prescription commitment. This is a Pharma compliance violation.",
    opts:[
      "A: It is not your business — your colleague will handle their own accounts",
      "B: Speak to your colleague privately and immediately — tell them what you heard, the risk it creates and ask them to correct it with the doctor",
      "C: Report it to your compliance team directly without speaking to your colleague first",
      "D: Wait to see if a complaint comes in before doing anything",
    ],
    most:1, least:3,
    exp_most:"Addressing it directly with the colleague first gives them the chance to correct it and is the proportionate first step.",
    exp_least:"Waiting for a complaint means patients may already be harmed by misinformation — inaction here is ethically indefensible."
  },
];
